import * as React from "react";
import {
  AggRecord,
  MultiHeaderTableRow,
  KeyValue,
  MultiHeaderTableData,
  Query,
  SearchPillData,
  SortDirection,
  SortProperties,
  Styles,
} from "../../interfaces";
import {
  defaultStructureChangeLevelTwoSortFields,
  oscrDataTemplate,
  oscrHeaders,
  oscrReportTypesDictionary,
  oscrTableHeaderDictionary,
} from "../../refdata/constants";
import {
  applyMissingFields,
  arraySum,
  createUrlSafePathing,
  fileNameFormat,
  getMaximumLengthOfHeaders,
  getObjectFromArray,
  dataDownloadHeaders,
} from "../../common/services/Utilities";
import { Divider, Typography } from "@mui/material";
import SearchPills from "../../containers/SearchPills";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import MultiHeaderTableBody from "../MultiHeaderTable/MultiHeaderTableBody";
import { encodeBrowserPathFromState } from "../../common/actions";
import ApiDataDownload from "../ApiDataDownload";
import { getHistoryAggregationApiCalls } from "../../common/services/HistoryUtilities";
import { fetchData } from "../../common/services/ApiClient";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import ShowHideButton from "../ShowHideButton";

interface Props {
  appState: Query;
  oscrSearchPillData: SearchPillData[];
  sortProperties: SortProperties;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  updateFilters: (filter: string, value: number[] | string | undefined) => void;
}

export function filterByEventNumber(
  data: AggRecord[],
  filteredEventCodes: number[],
) {
  return data.filter((aggData: AggRecord) => {
    return aggData && filteredEventCodes.includes(aggData.REPORT_TYPE);
  });
}

export function transformLevelOneAggregatedData(
  appState: Query,
  aggregatedDataResponse: AggRecord[],
  filteredEventCodes: number[],
  updateSort: (sortField: string, sortDirection: SortDirection) => void,
  updateFilters: (filter: string, value: number[] | string | undefined) => void,
  levelOneStyles: { [key: string]: Styles },
): MultiHeaderTableData {
  // loop through each category
  const aggregatedList: {
    [key: string]: number | string;
  }[] = Object.keys(oscrReportTypesDictionary).map((reportType: string) => {
    // find data for that category
    const filteredData: KeyValue[] = filterByEventNumber(
      aggregatedDataResponse,
      oscrReportTypesDictionary[reportType],
    ).map((aggRecord: AggRecord) => {
      const modifiedRecord: KeyValue = { ...aggRecord };
      delete modifiedRecord.REPORT_TYPE;

      return modifiedRecord;
    });

    return {
      title: reportType,
      ...applyMissingFields(oscrDataTemplate, arraySum(filteredData)),
    };
  });

  const OSCRSummaryTableData: MultiHeaderTableData = {};

  for (const bankClassVal in oscrTableHeaderDictionary) {
    OSCRSummaryTableData[bankClassVal] = oscrTableHeaderDictionary[bankClassVal]
      .filter((eventType: string) => {
        return filteredEventCodes.some(
          code => oscrReportTypesDictionary[eventType].indexOf(code) > -1,
        );
      })
      .map((eventType: string) => {
        const val = getObjectFromArray(aggregatedList, "title", eventType);

        return createLevelOneTableCellData(
          appState,
          val,
          levelOneStyles,
          updateSort,
          updateFilters,
        );
      });
  }

  return OSCRSummaryTableData;
}

export function createLevelOneTableCellData(
  appState: Query,
  data: { [key: string]: number | string },
  styles: { [key: string]: Styles },
  updateSort: (sortField: string, sortDirection: SortDirection) => void,
  updateFilters: (filter: string, value: number[] | string | undefined) => void,
): MultiHeaderTableRow {
  const tableCellData: MultiHeaderTableRow = {};

  Object.keys(data).forEach((key: string) => {
    const eventType = data.title as string;
    const oscrRecordCount =
      key === "title" || key === "count" ? data.count : data[key];

    const bankClassVal =
      key === "title" || key === "count" ? undefined : key.split("_").pop();

    const sortField = defaultStructureChangeLevelTwoSortFields[eventType]
      ? defaultStructureChangeLevelTwoSortFields[eventType]
      : "INSTNAME";

    const level2SearchParams =
      bankClassVal && !window.location.search
        ? `?bankClass=${bankClassVal}&sortField=${sortField}&sortOrder=ASC`
        : window.location.search
        ? encodeBrowserPathFromState({
            ...appState,
            filters: {
              ...appState.filters,
              bankClass: bankClassVal,
              eventCode: oscrReportTypesDictionary[eventType],
            },
            sort: {
              sortField,
              sortOrder: "ASC",
            },
          })
        : `?sortField=${sortField}&sortOrder=ASC`;

    tableCellData[key] = {
      value: data[key],
      styles: styles[key],
      leftAlign: key === "title",
      link: `/oscr/${createUrlSafePathing(eventType)}${level2SearchParams}`,
      onClick:
        (oscrRecordCount as number) !== 0
          ? generateOnClickFunctionForTableCell(
              oscrReportTypesDictionary[eventType],
              bankClassVal,
              sortField,
              updateSort,
              updateFilters,
            )
          : undefined,
    };
  });

  return tableCellData;
}

export function generateOnClickFunctionForTableCell(
  eventCode: number[],
  bankClassString: string | undefined,
  sortField: string,
  updateSort: (sortField: string, sortDirection: SortDirection) => void,
  updateFilters: (filter: string, value: number[] | string | undefined) => void,
) {
  return (event: React.MouseEvent) => {
    updateSort(sortField, "ASC");
    updateFilters("eventCode", eventCode);
    if (bankClassString) {
      updateFilters("bankClass", bankClassString);
    }
  };
}

export default function StructureChangeLevelOne({
  appState,
  oscrSearchPillData,
  sortProperties,
  updateSort,
  updateFilters,
}: Props) {
  const [levelOneState, setLevelOneState] = React.useState({
    createTimestamp: "",
    recordCount: 0,
    resultData: [],
  });
  const levelOneStyles: { [key: string]: Styles } = {
    count: {
      fontWeight: "bold",
    },
    title: {
      textAlign: "left",
    },
  };

  const showHideSearchPills = (
    <>
      <Divider />
      <ShowHideButton
        title="Selected Search Criteria"
        body={<SearchPills dataArray={oscrSearchPillData} />}
      />
    </>
  );

  React.useEffect(() => {
    if (appState.filters.bankClass) {
      updateFilters("bankClass", undefined);
    } else {
      const historyApiArray = getHistoryAggregationApiCalls(appState, true, "");
      let isMounted = true;
      (async () => {
        await Promise.all(
          historyApiArray.map((path: string) => fetchData(path)),
        )
          .then(response => {
            if (isMounted) {
              const responseData = [].concat(
                ...response.map(res => res.data as never[]),
              );
              const recordCount = response.reduce(
                (count, data) => count + data.totals.count,
                0,
              );

              setLevelOneState({
                resultData: responseData,
                recordCount: recordCount,
                createTimestamp: response[0].createTimestamp,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [appState, updateFilters]);

  const levelOneTransformedData = transformLevelOneAggregatedData(
    appState,
    levelOneState.resultData as AggRecord[],
    appState.filters.eventCode,
    updateSort,
    updateFilters,
    levelOneStyles,
  );

  const allHeaders = levelOneTransformedData.Institutions.concat(
    levelOneTransformedData.Offices,
  )[0];

  useInitialPageLoad("oscr");
  return (
    <>
      <Typography component="div" id="multi-header-table-title">
        <Typography
          variant="h2"
          id="resultSummary"
          style={{
            position: "relative",
          }}
        >
          Summary of Results
          <ApiDataDownload
            apiPathLink="history"
            app="structureChange"
            apiData={[
              {
                appState: appState,
                fileName: `${fileNameFormat("Summary of Results")}.csv`,
                apiPath: "history",
                headers: allHeaders
                  ? [].concat.apply(
                      [],
                      dataDownloadHeaders(
                        oscrHeaders,
                        levelOneTransformedData.Institutions.concat(
                          levelOneTransformedData.Offices,
                        )[0],
                      ) as any,
                    )
                  : undefined,
              },
            ]}
            downloadData={levelOneTransformedData.Institutions.concat(
              levelOneTransformedData.Offices,
            )}
          />
        </Typography>
        {showHideSearchPills}
      </Typography>
      <MultiHeaderTable
        sortProperties={sortProperties}
        tableHeaders={oscrHeaders}
        tableBody={
          <MultiHeaderTableBody
            createTimestamp={levelOneState.createTimestamp}
            tableData={levelOneTransformedData}
            maximumNumberOfColumns={getMaximumLengthOfHeaders(oscrHeaders)}
          />
        }
      />
    </>
  );
}
